/**
 * Setup form of the Options Simulator: strategies (with their own parameters, generated from
 * the server's strategy catalogue), date range, entry schedule, size, capital, margin mode and
 * cost scenarios. Owner defaults: 300 contracts per leg, ₹2.5 lakh, CROSS margin, base +
 * stressed costs.
 */
import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import type { SimCatalog, SimCoverage, SimParamSpec, SimStrategyInfo } from '../../services/optionsim.service';
import { fmt } from '../../utils/priceFormat';
import { isoDay } from './simFormat';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DEFAULT_STRATEGIES = ['shortStrangle', 'videoPremium', 'greekBand'];
/** One chart colour per strategy (styles/colorTokens chartSeries has 8 validated slots). */
const MAX_STRATEGIES = 8;
/** Delta India BTC option history starts in December 2023 (server rule, runConfig). */
const FIRST_DAY = '2023-12-01';

function lastFriday(): Date {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  while (d.getUTCDay() !== 5) d.setUTCDate(d.getUTCDate() - 1);
  return d;
}

export interface RunBody {
  kind: 'backtest' | 'compare';
  strategies: Array<{ id: string; params: Record<string, any> }>;
  from: string; to: string; res: 60 | 300; cadenceMin: number;
  entry: { kind: 'weekly' | 'daily'; weekdayUtc: number; hhmmUtc: string; windowMin: number };
  contractsPerLeg: number; capitalInr: number; marginMode: 'CROSS' | 'PM'; costScenarios: Array<'base' | 'stressed'>;
}

const ParamField: React.FC<{ spec: SimParamSpec; value: any; onChange: (v: any) => void }> = ({ spec, value, onChange }) => {
  const label = spec.label;
  if (spec.type === 'boolean') {
    return <FormControlLabel control={<Switch size="small" checked={!!value} onChange={e => onChange(e.target.checked)} />} label={<Typography variant="caption">{label}</Typography>} />;
  }
  if (spec.type === 'select') {
    return (
      <FormControl size="small" fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select label={label} value={value ?? ''} onChange={e => onChange(e.target.value)}>
          {(spec.options || []).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }
  if (spec.type === 'json') {
    return <TextField size="small" fullWidth multiline minRows={2} label={label} value={value ?? ''} helperText={spec.help} onChange={e => onChange(e.target.value || null)} />;
  }
  return (
    <Tooltip title={spec.help || ''} placement="top" arrow disableHoverListener={!spec.help}>
      <TextField size="small" fullWidth type="number" label={label} value={value ?? ''} placeholder={spec.nullable ? 'off' : undefined}
        inputProps={{ min: spec.min, max: spec.max, step: spec.step ?? 'any' }}
        onChange={e => onChange(e.target.value === '' ? (spec.nullable ? null : spec.default) : Number(e.target.value))} />
    </Tooltip>
  );
};

export const SimSetupCard: React.FC<{
  catalog: SimCatalog | null; coverage: SimCoverage | null; busy: boolean; onRun: (body: RunBody) => void;
}> = ({ catalog, coverage, busy, onRun }) => {
  const toD = lastFriday();
  const fromD = new Date(toD.getTime() - 26 * 7 * 86400_000);
  const [selected, setSelected] = useState<string[]>(DEFAULT_STRATEGIES);
  const [params, setParams] = useState<Record<string, Record<string, any>>>({});
  const [from, setFrom] = useState(isoDay(fromD.getTime() / 1000));
  const [to, setTo] = useState(isoDay(toD.getTime() / 1000));
  const [entryKind, setEntryKind] = useState<'weekly' | 'daily'>('weekly');
  const [weekday, setWeekday] = useState(5);
  const [hhmm, setHhmm] = useState('12:05');
  const [res, setRes] = useState<60 | 300>(300);
  const [cadence, setCadence] = useState(5);
  const [size, setSize] = useState(catalog?.defaults.contractsPerLeg ?? 300);
  const [capital, setCapital] = useState(catalog?.defaults.capitalInr ?? 250000);
  const [margin, setMargin] = useState<'CROSS' | 'PM'>('CROSS');
  const [costs, setCosts] = useState<Array<'base' | 'stressed'>>(['base', 'stressed']);

  const strategies = useMemo(() => catalog?.strategies ?? [], [catalog]);
  const byId = useMemo(() => new Map(strategies.map(s => [s.id, s])), [strategies]);
  const valueOf = (s: SimStrategyInfo, p: SimParamSpec) => (params[s.id] && p.key in params[s.id] ? params[s.id][p.key] : p.default);
  const setParam = (sid: string, key: string, v: any) => setParams(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [key]: v } }));
  const toggle = (id: string) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : prev.length >= MAX_STRATEGIES ? prev : [...prev, id]));
  const full = selected.length >= MAX_STRATEGIES;

  const withBars = (coverage?.expiries || []).filter(e => e.withBars > 0).length;
  const canRun = !busy && selected.length > 0 && costs.length > 0 && from < to;

  const run = () => onRun({
    kind: selected.length > 1 ? 'compare' : 'backtest',
    strategies: selected.map(id => ({ id, params: Object.fromEntries((byId.get(id)?.params || []).map(p => [p.key, valueOf(byId.get(id)!, p)])) })),
    from, to, res, cadenceMin: Math.max(cadence, res / 60),
    entry: { kind: entryKind, weekdayUtc: weekday, hhmmUtc: hhmm, windowMin: 360 },
    contractsPerLeg: size, capitalInr: capital, marginMode: margin, costScenarios: costs,
  });

  return (
    <Card sx={{ mb: 2 }} data-testid="optionsim-setup">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={800} gutterBottom>New simulation</Typography>

        <Typography variant="caption" fontWeight={700}>Strategies</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 1.5 }}>
          {strategies.map(s => (
            <Tooltip key={s.id} title={s.description} arrow>
              {/* the span keeps the tooltip working while the chip is disabled */}
              <Box component="span" sx={{ display: 'inline-flex', maxWidth: '100%' }}>
                <Chip size="small" label={s.label} clickable onClick={() => toggle(s.id)} disabled={full && !selected.includes(s.id)}
                  color={selected.includes(s.id) ? 'primary' : 'default'} variant={selected.includes(s.id) ? 'filled' : 'outlined'}
                  sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
              </Box>
            </Tooltip>
          ))}
          {!strategies.length && <Typography variant="caption" color="text.secondary">Loading strategies…</Typography>}
          {full && <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>Up to {MAX_STRATEGIES} strategies per run.</Typography>}
        </Box>

        {selected.map(id => {
          const s = byId.get(id);
          if (!s) return null;
          return (
            <Accordion key={id} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="caption" fontWeight={700} noWrap>{s.label}{s.usesEngine ? ' · engine' : ''}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.25}>
                  {s.params.map(p => <ParamField key={p.key} spec={p} value={valueOf(s, p)} onChange={v => setParam(s.id, p.key, v)} />)}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

        <Stack direction="row" spacing={1} sx={{ mt: 1.5, mb: 1.5 }}>
          <TextField size="small" type="date" label="From" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: FIRST_DAY, max: to }} fullWidth />
          <TextField size="small" type="date" label="To" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: from }} fullWidth />
        </Stack>

        <Typography variant="caption" fontWeight={700}>Entry (UTC)</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 1.5 }}>
          <ToggleButtonGroup size="small" exclusive value={entryKind} onChange={(_, v) => v && setEntryKind(v)}>
            <ToggleButton value="weekly">Weekly</ToggleButton>
            <ToggleButton value="daily">Daily</ToggleButton>
          </ToggleButtonGroup>
          {entryKind === 'weekly' && (
            <Select size="small" value={weekday} onChange={e => setWeekday(Number(e.target.value))} sx={{ minWidth: 76 }}>
              {WEEKDAYS.map((w, i) => <MenuItem key={w} value={i}>{w}</MenuItem>)}
            </Select>
          )}
          <TextField size="small" type="time" value={hhmm} onChange={e => setHhmm(e.target.value)} sx={{ minWidth: 96 }} inputProps={{ step: 300 }} />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="caption" fontWeight={700} sx={{ minWidth: 34 }}>Bars</Typography>
          <ToggleButtonGroup size="small" exclusive value={res} onChange={(_, v) => v && setRes(v)}>
            <ToggleButton value={300}>5m</ToggleButton>
            <ToggleButton value={60}>1m</ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Decide every</InputLabel>
            <Select label="Decide every" value={cadence} onChange={e => setCadence(Number(e.target.value))}>
              {[1, 5, 15, 30, 60].filter(m => m * 60 >= res).map(m => <MenuItem key={m} value={m}>{m} min</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Tooltip title="Contracts per leg (1 contract = 0.001 BTC). Your live baskets: 200–500; paper strangles: 1,000." arrow>
            <TextField size="small" type="number" label="Contracts / leg" value={size} onChange={e => setSize(Math.max(1, Math.round(Number(e.target.value) || 0)))} inputProps={{ step: 50, min: 1 }} fullWidth />
          </Tooltip>
          <Tooltip title="Capital per basket for margin use and return on capital." arrow>
            <TextField size="small" type="number" label="Capital (₹)" value={capital} onChange={e => setCapital(Math.max(10000, Number(e.target.value) || 0))} inputProps={{ step: 10000 }} fullWidth />
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" fontWeight={700}>Margin</Typography>
          <ToggleButtonGroup size="small" exclusive value={margin} onChange={(_, v) => v && setMargin(v)}>
            <Tooltip title="Your account's mode: Delta's tiered cross formula (option feed is an assumption, labelled)." arrow><ToggleButton value="CROSS">Cross</ToggleButton></Tooltip>
            <Tooltip title="Independent PM-shaped stress margin [A]; not Delta's number." arrow><ToggleButton value="PM">PM [A]</ToggleButton></Tooltip>
          </ToggleButtonGroup>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" fontWeight={700} sx={{ mr: 0.5 }}>Costs</Typography>
          {(['base', 'stressed'] as const).map(c => (
            <FormControlLabel key={c} sx={{ mr: 1 }} control={<Checkbox size="small" checked={costs.includes(c)} onChange={e => setCosts(p => (e.target.checked ? [...p, c] : p.filter(x => x !== c)))} />}
              label={<Typography variant="caption">{c === 'base' ? 'Base' : 'Stressed (2× spreads)'}</Typography>} />
          ))}
        </Stack>

        {coverage && !coverage.ready && <Alert severity="warning" sx={{ mb: 1.5 }}>{coverage.note}</Alert>}
        {coverage?.ready && (
          <Typography variant="caption" color={withBars ? 'text.secondary' : 'warning.main'} sx={{ display: 'block', mb: 1 }} data-testid="optionsim-coverage">
            Data: {fmt.num(withBars, 0)} of {fmt.num(coverage.expiries?.length ?? 0, 0)} Friday expiries have bars · index {fmt.num(coverage.index?.days ?? 0, 0)} days
            {!withBars ? ' — run npm run optionsim:backfill on the server first' : ''}
          </Typography>
        )}

        <Button fullWidth variant="contained" disabled={!canRun} onClick={run} data-testid="optionsim-run"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}>
          {selected.length > 1 ? `Compare ${selected.length} strategies` : 'Run simulation'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Replays Delta India BTC options bar by bar with no look-ahead. Fills are modelled (no historical bid/ask exists). Nothing is ever sent to a broker.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default SimSetupCard;
